/**
 * Add Website Dialog Context
 * Global state management for "Add New Website" dialog
 * Event-driven approach - works regardless of navigation state or login timing
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const AddWebsiteDialogContext = createContext();

export const useAddWebsiteDialogContext = () => {
  const context = useContext(AddWebsiteDialogContext);
  if (!context) {
    throw new Error('useAddWebsiteDialogContext must be used within AddWebsiteDialogProvider');
  }
  return context;
};

export const AddWebsiteDialogProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingPackageData, setPendingPackageData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Listen for package selection events (from anywhere in the app)
  useEffect(() => {
    const handlePackageSelect = (event) => {
      const { packageData, source } = event.detail || {};

      if (!packageData) return;

      // Store package data
      setPendingPackageData(packageData);
      setIsProcessing(true);

      // If user is logged in, show dialog immediately
      if (user && !authLoading) {
        setShowDialog(true);
      } else {
        try {
          sessionStorage.setItem('pending_add_website', JSON.stringify({
            packageData,
            timestamp: Date.now(),
            source: source || 'event'
          }));
        } catch (_e) {
          // sessionStorage unavailable
        }
        // If auth loading or not logged in, dialog will show when ready or after login
      }
    };

    // Listen for custom event
    window.addEventListener('addWebsitePackageSelected', handlePackageSelect);

    return () => {
      window.removeEventListener('addWebsitePackageSelected', handlePackageSelect);
    };
  }, [user, authLoading]);

  // Check sessionStorage when user logs in
  useEffect(() => {
    if (!user || authLoading || isProcessing) {
      return;
    }

    // User just logged in - check if there's pending package data
    try {
      const stored = sessionStorage.getItem('pending_add_website');
      if (stored) {
        const parsed = JSON.parse(stored);
        const age = Date.now() - parsed.timestamp;
        
        // Use if less than 10 minutes old
        if (age < 10 * 60 * 1000) {
          setPendingPackageData(parsed.packageData);
          setShowDialog(true);
          setIsProcessing(true);
          sessionStorage.removeItem('pending_add_website');
        } else {
          sessionStorage.removeItem('pending_add_website');
        }
      }
    } catch (_e) {
      // sessionStorage unavailable or invalid
    }
  }, [user, authLoading, isProcessing]);

  // Close dialog handler
  const closeDialog = useCallback(() => {
    setShowDialog(false);
    setIsProcessing(false);
    setPendingPackageData(null);
  }, []);

  // Manual trigger function (can be called from anywhere)
  const triggerDialog = useCallback((packageData) => {
    if (!packageData) return;

    setPendingPackageData(packageData);
    setIsProcessing(true);

    if (user && !authLoading) {
      setShowDialog(true);
    } else {
      // Store for later
      try {
        sessionStorage.setItem('pending_add_website', JSON.stringify({
          packageData,
          timestamp: Date.now(),
          source: 'manual'
        }));
      } catch (_e) {
        // sessionStorage unavailable
      }
    }
  }, [user, authLoading]);

  const value = {
    showDialog,
    pendingPackageData,
    closeDialog,
    triggerDialog,
    isProcessing
  };

  return (
    <AddWebsiteDialogContext.Provider value={value}>
      {children}
    </AddWebsiteDialogContext.Provider>
  );
};


